import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

type IdpConfigurationStep = 'clients' | 'provider' | 'resources' | 'scopes'

const steps: { key: IdpConfigurationStep; path: string }[] = [
  { key: 'provider', path: '/admin/idp/provider' },
  { key: 'scopes', path: '/admin/idp/client-scopes' },
  { key: 'resources', path: '/admin/idp/resources' },
  { key: 'clients', path: '/admin/idp/clients' },
]

export function IdpConfigurationFlow({ active }: { active: IdpConfigurationStep }) {
  const { t } = useTranslation('adminIdp')
  const navigate = useNavigate()

  return (
    <nav
      aria-label={t('setupFlow.label')}
      className="grid overflow-hidden rounded-xl border border-[var(--color-panel-border)] bg-white sm:grid-cols-2 xl:grid-cols-4"
    >
      {steps.map((step, index) => {
        const isActive = step.key === active
        return (
          <button
            aria-current={isActive ? 'step' : undefined}
            className={cn(
              'flex min-w-0 items-start gap-3 border-b border-[var(--color-divider)] px-4 py-3 text-left transition-colors last:border-b-0 sm:[&:nth-child(odd)]:border-r xl:border-r xl:border-b-0 xl:last:border-r-0',
              isActive ? 'bg-[rgba(25,91,255,0.065)]' : 'hover:bg-[var(--color-hover-muted)]',
            )}
            key={step.key}
            onClick={() => void navigate({ to: step.path })}
            type="button"
          >
            <span
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                isActive
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]',
              )}
            >
              {index + 1}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold">
                {t(`setupFlow.steps.${step.key}.title`)}
              </span>
              <span className="mt-0.5 block text-xs leading-5 text-[var(--color-text-muted)]">
                {t(`setupFlow.steps.${step.key}.description`)}
              </span>
            </span>
          </button>
        )
      })}
    </nav>
  )
}
